import { cloneDeep } from "lodash";
import { apiSlice } from "./apiSlice";
import store from "src/_store/store"

export const warehouseApi = apiSlice
    .enhanceEndpoints({ addTagTypes: ['WAREHOUSE', 'ADMIN-WAREHOUSE'] })
    .injectEndpoints({
        endpoints: builder => ({
            getWarehouses: builder.query({
                query: (params) => ({
                    url: '/warehouse',
                    method: 'GET',
                    params
                }),
                transformResponse: (response, meta, arg) => {
                    const state = store.getState()
                    let customizations = cloneDeep(state?.entities?.customizations)
                    if (!!customizations?.warehouse?.id) {
                        response.body.docs = response?.body?.docs?.filter(el => el?.id === customizations?.warehouse?.id)
                        return response?.body
                    }
                    return response?.body
                },
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
                providesTags: (result, _, __) =>
                    result
                        ? [
                            ...result?.docs.map(({ id }) => ({ type: "WAREHOUSE", id })),
                            { type: "WAREHOUSE", id: "WAREHOUSE-PARTIAL-LIST" },
                        ]
                        : [{ type: "WAREHOUSE", id: "WAREHOUSE-PARTIAL-LIST" }],
            }),
            getWarehousesWithoutCustomizations: builder.query({
                query: (params) => ({
                    url: '/warehouse',
                    method: 'GET',
                    params
                }),
                providesTags: (result, _, __) =>
                    result
                        ? [
                            ...result?.docs.map(({ id }) => ({ type: "WAREHOUSE", id })),
                            { type: "WAREHOUSE", id: "WAREHOUSE-PARTIAL-LIST" },
                        ]
                        : [{ type: "WAREHOUSE", id: "WAREHOUSE-PARTIAL-LIST" }],
                transformResponse: (response, meta, arg) => response?.body,
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            getWarehouseById: builder.query({
                query: (id) => ({
                    url: `/warehouse/${id}`,
                    method: 'GET'
                }),
                providesTags: (_, __, id) => [{ type: 'WAREHOUSE', id }],
                transformResponse: (response, meta, arg) => response?.body,
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            getWarehousesByFilter: builder.query({
                query: (params) => ({
                    url: '/warehouse/filter',
                    method: 'GET',
                    params
                }),
                providesTags: (result, _, __) =>
                    result
                        ? [
                            ...result?.docs.map(({ id }) => ({ type: "WAREHOUSE", id })),
                            { type: "WAREHOUSE", id: "WAREHOUSE-PARTIAL-LIST" },
                        ]
                        : [{ type: "WAREHOUSE", id: "WAREHOUSE-PARTIAL-LIST" }],
                transformResponse: (response, meta, arg) => response?.body,
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            createWarehouse: builder.mutation({
                query: (payload) => {
                    return {
                        url: '/warehouse',
                        method: 'POST',
                        body: { ...payload }
                    }
                },
                invalidatesTags: [{ type: 'WAREHOUSE', id: 'WAREHOUSE-PARTIAL-LIST' }],
                transformResponse: (response, meta, arg) => response?.body,
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            updateWarehouse: builder.mutation({
                query: (payload) => {
                    return {
                        url: '/warehouse/update',
                        method: 'POST',
                        body: { ...payload }
                    }
                },
                invalidatesTags: [{ type: 'WAREHOUSE', id: 'WAREHOUSE-PARTIAL-LIST' }],
                transformResponse: (response, meta, arg) => response?.body,
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            deleteWarehouse: builder.mutation({
                query: (id) => ({
                    url: `/warehouse/${id}`,
                    method: 'DELETE'
                }),
                invalidatesTags: [{ type: 'WAREHOUSE', id: 'WAREHOUSE-PARTIAL-LIST' }],
                transformResponse: (response, meta, arg) => response?.body,
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            restoreWarehouse: builder.mutation({
                query: (payload) => ({
                    url: '/warehouse/restore',
                    method: 'PUT',
                    body: { ...payload }
                }),
                invalidatesTags: [{ type: 'WAREHOUSE', id: 'WAREHOUSE-PARTIAL-LIST' }],
                transformResponse: (response, meta, arg) => response?.body,
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            getWarehousesByAdmin: builder.query({
                query: (params) => ({
                    url: "/warehouse/filter-by-admin",
                    method: 'GET',
                    params
                }),
                providesTags: (result, _, __) =>
                    result
                        ? [
                            ...result?.docs.map(({ id }) => ({ type: "ADMIN-WAREHOUSE", id })),
                            { type: "ADMIN-WAREHOUSE", id: "ADMIN-WAREHOUSE-PARTIAL-LIST" },
                        ]
                        : [{ type: "ADMIN-WAREHOUSE", id: "ADMIN-WAREHOUSE-PARTIAL-LIST" }],
                transformResponse: (response, meta, arg) => response?.body,
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            getWarehousesManualStatus: builder.query({
                query: () => ({
                    url: "/warehouse/manual",
                    method: 'GET',
                }),
                transformResponse: (response, meta, arg) => response?.body,
                transformErrorResponse: (response, meta, arg) => response?.data?.message ?? "Something went wrong please try again",
            }),
            updateWarehouseAdmin: builder.mutation({
                query: ({ id, payload }) => ({
                    url: `/warehouse/${id}`,
                    method: "PUT",
                    body: { ...payload }
                }),
                invalidatesTags: (result, error, { id }) => [{ type: 'ADMIN-WAREHOUSE', id }, { type: 'ADMIN-WAREHOUSE', id: 'ADMIN-WAREHOUSE-PARTIAL-LIST' }],
            })
        })
    })

export const {
    useGetWarehousesQuery,
    useLazyGetWarehousesQuery,
    useGetWarehouseByIdQuery,
    useLazyGetWarehousesByFilterQuery,
    useGetWarehousesByFilterQuery,
    useCreateWarehouseMutation,
    useUpdateWarehouseMutation,
    useDeleteWarehouseMutation,
    useRestoreWarehouseMutation,
    useGetWarehousesWithoutCustomizationsQuery,
    useGetWarehousesByAdminQuery,
    useLazyGetWarehousesManualStatusQuery,
    useGetWarehousesManualStatusQuery,
    useUpdateWarehouseAdminMutation
} = warehouseApi
